import * as React from "react";
import Emojify from "app/components/shared/Emojify";
import { shortCommit } from "app/lib/commits";
import classNames from "classnames";
import { jobTime } from "app/lib/jobs";
import Duration from "app/components/shared/Duration";
import Icon from "app/components/shared/Icon";
import PipelineStateIcon from "app/components/shared/PipelineStateIcon";
import { elementIdFor } from "app/components/job/Job";
import { TriggerJob } from "../Header/pipeline/types/TriggerJob";

type Props = {
  className?: string;
  job: TriggerJob;
  isRetry: boolean | null | undefined;
};

export default class TriggerJobSummary extends React.PureComponent<Props> {
  render() {
    let state =
      this.props.job.state === "finished" ||
      this.props.job.state === "timed_out" ||
      this.props.job.state === "canceled" ||
      this.props.job.state === "expired"
        ? this.props.job.passed
          ? "passed"
          : "failed"
        : this.props.job.state;

    // Canceling and timing out are still running, so let's show them as such.
    if (state === "canceling" || state === "timing_out") {
      state = "running";
    }

    let contentsNode = (
      <div className="build-details-pipeline-job__header flex items-center p3 bg-white z2 hover-bg-silver">
        <span className="build-details-pipeline-icon mr1">
          <PipelineStateIcon job={this.props.job} />
        </span>
        <div className="flex flex-auto xs-flex-wrap items-center mr1">
          <span className="mr2">
            <Emojify text={this.props.job.name || "Trigger Build"} />
          </span>
          <span className="dark-gray">
            {this.props.isRetry ? "Rebuild" : "Triggered build"}
            {" of "}
            <code className="monospace">{this.props.job.triggerBranch}</code>
            {" \x2F "}
            <code className="monospace" title={this.props.job.triggerCommit}>
              {shortCommit(
                this.props.job.triggerCommit,
                this.props.job.triggerCommitShortLength,
              )}
            </code>
            {" on "}
            {this.nameNode()}
          </span>
        </div>
        {this.durationNode()}
        {this.errorMessageNode()}
        {this.deletedNode()}
        <div className="flex-none dark-gray pl3">
          <Icon icon="chevron-right" style={{ height: 12, width: 12 }} />
        </div>
      </div>
    );

    const classes = classNames(
      "build-details-pipeline-job relative color-inherit focus-color-inherit text-decoration-none hover-color-inherit block",
      {
        [`build-details-pipeline-job-state-${state}`]: true,
      },
      this.props.className,
    );

    if (
      this.props.job.triggeredBuild &&
      !this.props.job.triggeredBuild.destroyedAt
    ) {
      contentsNode = (
        <a
          href={this.props.job.triggeredBuild.url}
          className={classes}
          id={this.elementId()}
        >
          {contentsNode}
        </a>
      );
    } else {
      contentsNode = (
        <div className={classes} id={this.elementId()}>
          {contentsNode}
        </div>
      );
    }

    return contentsNode;
  }

  nameNode() {
    // triggerProject might be soft-deleted, in which case it has a destroyedAt.
    // If triggerProject has been hard-deleted, it will be null/undefined/nada.
    // However triggerProjectSlug etc remain stored on the job, so those remain.
    if (this.props.job.triggerProject) {
      return <Emojify text={this.props.job.triggerProject.name} />;
    }
    if (this.props.job.triggerProjectSlug) {
      return (
        <code className="monospace">{this.props.job.triggerProjectSlug}</code>
      );
    }
    return "unknown pipeline";
  }

  durationNode() {
    const buildNumber = this.props.job.triggeredBuild
      ? `#${this.props.job.triggeredBuild.number}`
      : "";

    if (this.props.job.triggerAsync) {
      return (
        <div className="xs-flex-wrap sm-flex-none dark-gray pl2">
          Build {buildNumber} (asynchronous)
        </div>
      );
    }

    if (this.props.job.startedAt) {
      const runTense =
        this.props.job.state === "running" ? "running for" : "ran in";

      const duration = jobTime({
        state: this.props.job.state.toUpperCase(),
        startedAt: this.props.job.startedAt,
        finishedAt: this.props.job.finishedAt,
        canceledAt: this.props.job.canceledAt,
        timedOutAt: this.props.job.timedOutAt,
      });

      return (
        <div className="xs-flex-wrap sm-flex-none dark-gray pl2">
          <i className="fa fa-clock-o mr1" />
          <span>
            Build {buildNumber} {runTense} <Duration.Short {...duration} />
          </span>
        </div>
      );
    }
  }

  deletedNode() {
    const job = this.props.job;
    return (
      (job.triggeredBuildDeleted === true ||
        (job.triggeredBuild && job.triggeredBuild.destroyedAt)) && (
        <div className="flex-none ml1 red">(Deleted)</div>
      )
    );
  }

  errorMessageNode() {
    return (
      this.props.job.triggerFailedReason && (
        <div className="flex-none red max-width-3 ml1">
          <i className="fa fa-warning" /> {this.props.job.triggerFailedReason}
        </div>
      )
    );
  }

  elementId() {
    return elementIdFor(this.props.job.id);
  }
}
